import { isProgressAltPeriodId, type ProgressAltPeriodId } from './progressAltPeriods';

/**
 * The shape of the grid, as the URL carries it.
 *
 * Which scope the panel is looking at is named by the route and which cell by `progressAltCellUrl`.
 * What is left is how the grid is arranged around that cell — how wide its rows are cut, and which of
 * them are folded away — which has no route slot either, so it rides in the query string. The point of
 * it being there is that the URL reconstructs the screen: reload it, bookmark it, send it to someone,
 * and the grid comes back cut and folded the way it was left.
 */

export const PROGRESS_ALT_GROUP_BY_PARAM = 'groupBy';

/**
 * The undated row's own parameter, no grouping in its name.
 *
 * Photos with no date gather into a single leading row under every grouping, so its folded state is
 * not a fact about any one of them — regrouping leaves that row exactly where it was, and this leaves
 * it as it was found. A flag rather than a number, there being only ever one such row.
 */
export const PROGRESS_ALT_COLLAPSED_UNDATED_PARAM = 'collapsedUndated';

const COLLAPSED_UNDATED_VALUE = '1';

/**
 * One parameter per grouping, which is what spares them all any translating.
 *
 * A row is named by its number — the ordinal the date column shows, counted in the unit the grouping
 * names — and that number means something different under each. Week 5 and day 5 are not the same
 * stretch of the renovation, and month 1 and day 1 open at the very same instant. Kept in one
 * parameter they would have to be converted on every change of grouping, and a week does not convert
 * into days without inventing an answer for which of the seven were meant. Kept apart they need no
 * converting at all: each grouping reads its own, so going to the week and back finds the days folded
 * exactly as they were.
 */
const COLLAPSED_ROWS_PARAM_BY_PERIOD_ID: Record<ProgressAltPeriodId, string> = {
  day: 'collapsedDays',
  week: 'collapsedWeeks',
  month: 'collapsedMonths',
  minute: 'collapsedMinutes',
};

/**
 * A folded row, named the way the date column names it: its ordinal, or the undated row's stand-in.
 *
 * The same token `timelineAt` uses for the same row, so the two halves of the address spell it alike.
 */
export const UNDATED_ROW_NUMBER = 'undated';
export type ProgressAltRowNumber = number | typeof UNDATED_ROW_NUMBER;

/**
 * Which grouping the URL asks for, or `fallback` when it names none.
 *
 * Untrusted, like every other read here: an unrecognized grouping is a fallback rather than an error,
 * so a stale or hand-edited link still opens the grid.
 */
export const readProgressAltGroupBy = (
  params: URLSearchParams,
  fallback: ProgressAltPeriodId,
): ProgressAltPeriodId => {
  const raw = params.get(PROGRESS_ALT_GROUP_BY_PARAM);
  return raw != null && isProgressAltPeriodId(raw) ? raw : fallback;
};

/** Ordinals count from one, so anything else in the list is nothing this grid could have folded. */
const parseRowNumber = (raw: string): number | undefined => {
  const rowNumber = Number(raw);
  return Number.isInteger(rowNumber) && rowNumber > 0 ? rowNumber : undefined;
};

/**
 * Which rows the URL asks to be folded under this grouping, undated row included.
 *
 * Malformed entries are dropped rather than thrown over: the rest of the list is still a legitimate
 * address, and a row that cannot be named is a row left open.
 */
export const readProgressAltCollapsedRows = (
  params: URLSearchParams,
  periodId: ProgressAltPeriodId,
): ReadonlySet<ProgressAltRowNumber> => {
  const collapsedRows = new Set<ProgressAltRowNumber>();
  if (params.has(PROGRESS_ALT_COLLAPSED_UNDATED_PARAM)) collapsedRows.add(UNDATED_ROW_NUMBER);
  const raw = params.get(COLLAPSED_ROWS_PARAM_BY_PERIOD_ID[periodId]);
  for (const entry of raw ? raw.split(',') : []) {
    const rowNumber = parseRowNumber(entry.trim());
    if (rowNumber != null) collapsedRows.add(rowNumber);
  }
  return collapsedRows;
};

/**
 * The same params with this grouping written in.
 *
 * Written even when it is the one the panel would have opened on anyway, so the URL says what the
 * screen shows rather than leaving it to be inferred from a default that `?debug` could move.
 */
export const writeProgressAltGroupBy = (
  params: URLSearchParams,
  periodId: ProgressAltPeriodId,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.set(PROGRESS_ALT_GROUP_BY_PARAM, periodId);
  return next;
};

/**
 * The same params with this grouping's folded rows written in, or cleared out when none are folded.
 *
 * Leaves every other param alone, this grouping's siblings among them — that is what keeps a trip
 * through another grouping from disturbing the rows folded here. Ordinals go in ascending order so
 * the same set of rows always spells the same URL.
 */
export const writeProgressAltCollapsedRows = (
  params: URLSearchParams,
  periodId: ProgressAltPeriodId,
  collapsedRows: ReadonlySet<ProgressAltRowNumber>,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  if (collapsedRows.has(UNDATED_ROW_NUMBER)) {
    next.set(PROGRESS_ALT_COLLAPSED_UNDATED_PARAM, COLLAPSED_UNDATED_VALUE);
  } else {
    next.delete(PROGRESS_ALT_COLLAPSED_UNDATED_PARAM);
  }
  const rowNumbers = [...collapsedRows]
    .filter((rowNumber): rowNumber is number => rowNumber !== UNDATED_ROW_NUMBER)
    .sort((a, b) => a - b);
  const param = COLLAPSED_ROWS_PARAM_BY_PERIOD_ID[periodId];
  if (rowNumbers.length) next.set(param, rowNumbers.join(','));
  else next.delete(param);
  return next;
};
